import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { gatePeriodLabel } from "./payoutCurve";
import "./gatePopover.css";

/**
 * Why the bonus is paying what it is paying.
 *
 * The card says WHAT in one line; this says WHY, always in the same order: how
 * many gates are cleared, what is paying and what it unlocks, one sentence, the
 * gates themselves, and the single move that gets them closest.
 *
 * Colour walks a four-step ladder — nothing cleared, moving, one to go,
 * unlocked — and never reaches red. Mid-period nobody has failed; they have not
 * arrived. Somebody sitting on three of four should see that they are nearly
 * there, not a wall of alarm.
 *
 * It portals to body because the curve box is `overflow: hidden` and would clip
 * it, and flips ABOVE the line when the card sits near the bottom of the
 * viewport.
 */

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

/** A gate's own numbers, in the unit the admin chose for it. */
const gateNum = (v, unit) => {
  if (v == null || v === "" || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  return unit === "$" ? `$${Math.round(n).toLocaleString()}` : `${Math.round(n)}%`;
};

/* How often a manual gate has to be signed off again, said plainly. Building
   this by suffixing "ly" onto the period label produced "custom rangely". */
const confirmEvery = (p) => {
  if (p === "year") return "confirmed each year";
  if (p === "month") return "confirmed each month";
  if (p === "custom") return "confirmed each range";
  return "confirmed each quarter";
};

/** Which rung of the ladder one gate sits on. Met is green; short is coloured
    by how close it is, so 96 of 100 does not look like 12 of 100. */
const gateStep = (s) => {
  if (s.state === "met") return "s3";
  if (s.state === "unbound") return "s0";
  if ((s.pct || 0) >= 0.85) return "s2";
  if ((s.pct || 0) > 0) return "s1";
  return "s0";
};

/* The line that lives on the card under the curve. One sentence, so nobody has
   to click to know where they stand. The segmented meter is deliberately NOT
   here — it lives in the popover, where it has a row to itself. */
export const GateLine = ({ step, text, open, onToggle }) => (
  <div
    className="cw-gline"
    role="button"
    tabIndex={0}
    aria-expanded={open}
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onToggle(); }
    }}
  >
    <span className={`cw-gdot ${step}`} />
    <p>{text}</p>
    <span className="cw-gsee">{open ? "Hide" : "See why"}</span>
  </div>
);
GateLine.propTypes = {
  step: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const GatePopover = ({
  anchorEl, onClose, tierLabel, paying, unlocks, cleared, total, step, say, states, foot,
}) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  /* Measure, then place. Below the line by default; above it when the space
     below cannot hold the popover — the case for any card near the bottom. */
  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      const a = anchorEl;
      if (!el || !a) return;
      const r = a.getBoundingClientRect();
      const h = el.offsetHeight;
      const w = el.offsetWidth;
      const GAP = 9;
      const PAD = 10;
      const room = window.innerHeight - r.bottom;
      const flip = room < h + GAP + PAD && r.top > room;
      const top = flip
        ? Math.max(PAD, r.top - h - GAP)
        : Math.min(window.innerHeight - h - PAD, r.bottom + GAP);
      const left = Math.max(PAD, Math.min(window.innerWidth - w - PAD, r.right - w));
      setPos({ top, left, flip, beak: Math.max(14, Math.min(w - 26, r.right - left - 46)) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorEl, states.length]);

  useEffect(() => {
    const away = (e) => {
      if (ref.current && !ref.current.contains(e.target) && anchorEl && !anchorEl.contains(e.target)) onClose();
    };
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [anchorEl, onClose]);

  const paid = cleared >= total;

  return createPortal(
    <div
      ref={ref}
      className={`cw-pop${pos && pos.flip ? " up" : ""}`}
      style={pos ? { top: pos.top, left: pos.left, visibility: "visible" } : { top: 0, left: 0, visibility: "hidden" }}
    >
      <span className="cw-beak" style={pos ? { right: pos.beak } : undefined} />
      <div className="cw-sheet">
        <div className="cw-phead">
          <b>{`What the ${tierLabel} tier needs`}</b>
          <button type="button" className="cw-px" aria-label="Close" onClick={onClose}>&times;</button>
        </div>
        <div className="cw-pbody">
          {/* Count first, and in the ladder's colour — the fastest read there is. */}
          <div className="cw-cleared">
            <span className="cw-meter" aria-hidden>
              {Array.from({ length: total }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={i} className={`cw-seg${i < cleared ? ` on ${step}` : ""}`} />
              ))}
            </span>
            <span className={`cw-count ${step}`}>{`${cleared} of ${total} cleared`}</span>
          </div>

          <div className="cw-tiles">
            <div className="cw-tile">
              <div className="cw-k">Paying now</div>
              <div className={`cw-v${paid ? " s3" : ""}`}>{money(paying)}</div>
            </div>
            {!paid && (
              /* Names the prize, not the punishment. Same number either way. */
              <div className={`cw-tile goal ${step}`}>
                <div className="cw-k">{`Unlocks at ${total} of ${total}`}</div>
                <div className={`cw-v ${step}`}>{money(unlocks)}</div>
              </div>
            )}
          </div>
          <p className="cw-say">{say}</p>

          <div className="cw-ghead">
            <span className="cw-h">Gate</span>
            <span className="cw-c">Now</span>
            <span className="cw-c">Minimum</span>
            <span className="cw-c">Target</span>
          </div>
          {states.map((s, i) => {
            const g = s.gate || {};
            const gs = gateStep(s);
            const manual = g.source === "manual";
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div className="cw-grow" key={i}>
                <span className={`cw-mk ${gs}`}>{s.state === "met" ? "✓" : "•"}</span>
                <span className="cw-gid">
                  <b>{g.name || g.metricName || "Gate"}</b>
                  <span className="cw-per">{manual ? confirmEvery(g.period) : gatePeriodLabel(g.period).toLowerCase()}</span>
                  {/* How far along this one gate is, on its own. */}
                  {!manual && s.state !== "unbound" && (
                    <span className="cw-fill">
                      <span className={`cw-fillbar ${gs}`} style={{ width: `${Math.round((s.pct || 0) * 100)}%` }} />
                    </span>
                  )}
                </span>
                {manual || s.state === "unbound" ? (
                  <span className={`cw-span ${gs}`}>
                    {(() => {
                      if (s.state === "met") return s.confirmedBy ? `Confirmed by ${s.confirmedBy}` : "Confirmed";
                      if (s.state === "unbound") return "Not set up yet";
                      return "Not confirmed yet";
                    })()}
                  </span>
                ) : (
                  <>
                    <span className={`cw-c now ${gs}`}>{gateNum(s.value, g.unit)}</span>
                    <span className="cw-c">{gateNum(s.min, g.unit)}</span>
                    <span className="cw-c dim">{gateNum(g.target, g.unit)}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {foot ? <div className="cw-pfoot"><p>{foot}</p></div> : null}
      </div>
    </div>,
    document.body,
  );
};

GatePopover.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  onClose: PropTypes.func.isRequired,
  tierLabel: PropTypes.string.isRequired,
  paying: PropTypes.number.isRequired,
  unlocks: PropTypes.number.isRequired,
  cleared: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  step: PropTypes.string.isRequired,
  say: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  states: PropTypes.array.isRequired,
  foot: PropTypes.string,
};
GatePopover.defaultProps = { anchorEl: null, foot: "" };

export default GatePopover;
